import { Box, Loader } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ITocRequestData } from '@common/validation/customControls/ophthalmology';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ProcedureStatus, RequestStatus, ServiceCode, ServiceType } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { ProcedureDiagnosticReportReadButtons } from '@frontend/components/elements/carePathButtons';
import { FormLabel, HorizontalFormGroupRead, InputContainer } from '@frontend/components/elements/forms';
import {
    checkIsReferralCheckCompleted,
    checkIsReferralCheckRequired,
} from '@frontend/components/pages/customControls/TOO/v1/serviceConditionalsService';
import { getActiveInsuranceFromInsuranceHistory } from '@frontend/services/api/patientInsuranceApiService';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IService } from '../../../../interfaces/service';
import type { ICustomControlProcedureDiagnosticReportReadProps } from '../../interfaces/';
import { FormDivider, FormParagraph } from '../../../../elements/common';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { TooV1DiagnosticReportRead } from './TooV1DiagnosticReportRead';
import { TooV1ProcedureRead } from './TooV1ProcedureRead';

export function TooV1ProcedureDiagnosticReportRead(props: ICustomControlProcedureDiagnosticReportReadProps) {
    const { request } = props;
    const abortSignal = useAbortSignal();
    const translate = getSelectedLanguage();
    const [isConsultOpen, setIsConsultOpen] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isReferralCheckCompleted, setIsReferralCheckCompleted] = useState<boolean | undefined>(undefined);
    const [tocService, setTocService] = useState<IService | null>(null);

    const renderConvertToConsultButton = !props.procedure.customControlData.consult ? (
        <ProcedureDiagnosticReportReadButtons
            {...props}
            hideConvertToConsultButton={true}
        />
    ) : undefined;

    useEffect(() => {
        initializeComponent();
    }, []);

    async function initializeComponent() {
        if (props.procedure.status === ProcedureStatus.inProgress) {
            await loadReferralCheck();
        }
        await loadTocService();
        setIsInitializing(false);
    }

    async function loadReferralCheck() {
        const activeInsurance = await getActiveInsuranceFromInsuranceHistory(request.patientId, abortSignal);
        const isReferralCheckRequired = checkIsReferralCheckRequired({
            activeInsurance,
            isDirectProcedureAdded: request.isDirectProcedureAdded,
        });
        const referralCheckCompleted = await checkIsReferralCheckCompleted({
            customControlData: props.procedure.customControlData,
            isDirectProcedureAdded: request.isDirectProcedureAdded,
            isReferralCheckRequired: isReferralCheckRequired,
            organizationId: request.draftCareProviderOrganizationId,
            patientId: request.patientId,
            practitionerId: request.draftCareProviderPractitionerId,
        });
        setIsReferralCheckCompleted(referralCheckCompleted);
    }

    async function loadTocService() {
        const result = await fetchJson(`api/services?code=${ServiceCode.TOC}`, 'GET');
        if (result.json.items) {
            setTocService(result.json.items[0]);
        }
    }

    function tocRequestRead() {
        const consultData: ITocRequestData = props.procedure.customControlData.consult;

        if (request.status === RequestStatus.completed || !tocService || !consultData) {
            return null;
        }

        return (
            <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
                <ProcessItemHeader
                    isFirstItem={props.isFirstItem}
                    isFirstProcess={props.itemState.isFirstProcess}
                    isItemOpen={isConsultOpen}
                    onToggle={async (isOpened) => setIsConsultOpen(isOpened)}
                    organizationName={props.process.recipientOrganization?.name}
                    practitionerName={
                        props.process.recipientPractitioner
                            ? getDisplayName(props.process.recipientPractitioner)
                            : undefined
                    }
                    process={props.process}
                    serviceType={ServiceType.consultation}
                    showProcessIcon
                    title={getServiceLabel(tocService.code, 'request', tocService.name)}
                />

                {consultData.question && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Vraag</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{consultData.question}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <CustomControlCollapseBody isOpen={isConsultOpen}>
                    {consultData.toelichting && (
                        <>
                            <FormDivider />
                            <HorizontalFormGroupRead>
                                <FormLabel>Toelichting</FormLabel>
                                <InputContainer>
                                    <FormParagraph bold>{consultData.toelichting}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        </>
                    )}
                </CustomControlCollapseBody>
                {consultData.toelichting && (
                    <ProcessItemFooter
                        isItemOpen={isConsultOpen}
                        labelLess={translate.request.hideConsult}
                        labelMore={translate.request.showConsult}
                        onToggle={async (isOpened) => setIsConsultOpen(isOpened)}
                    />
                )}
                <ProcedureDiagnosticReportReadButtons
                    {...props}
                    hideConvertToConsultButton={true}
                    hidePrintButton={true}
                />
            </ProcessContainer>
        );
    }

    if (isInitializing) {
        return (
            <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                paddingY={6}
            >
                <Loader />
            </Box>
        );
    }

    return (
        <>
            <TooV1ProcedureRead
                isFirstItem={props.isFirstItem}
                isLastItem={false}
                isReferralCheckCompleted={isReferralCheckCompleted}
                itemState={props.itemState}
                procedure={props.procedure}
                process={props.process}
                request={request}
                serviceCode={props.serviceCode}
                supervision={props.supervision}
                updateItemState={props.updateItemState}
            />
            <TooV1DiagnosticReportRead
                diagnosticReport={props.diagnosticReport}
                isFirstItem={false}
                isLastItem={false}
                isReferralCheckCompleted={isReferralCheckCompleted}
                itemState={props.reportItemState}
                process={props.process}
                processButtons={renderConvertToConsultButton}
                request={request}
                scrollToProcessItem={props.scrollToProcessItem}
                serviceCode={props.serviceCode}
                setHideCreateActions={props.setHideCreateActions}
                updateItemState={props.updateReportItemState}
            />
            {props.procedure.customControlData.consult && tocRequestRead()}
        </>
    );
}
